import { Toaster } from "@agrigate/shared-web-ui/lib/components/ui/toaster";
import { useHashRouteRedirect } from "@agrigate/shared-web-ui/lib/hooks/use-hash-route-redirect";
import { useIsDesktop } from "@agrigate/shared-web-ui/lib/hooks/use-is-desktop";
import { Box, Flex, Image, Link } from "@chakra-ui/react";
import { useAtom, useSetAtom } from "jotai";
import { type ReactElement, Suspense, useEffect, useState } from "react";
import { useLocation, useRoutes } from "react-router";
import routes from "~react-pages";
import appLogo from "../assets/app-logo.png";
import compactLogo from "../assets/company-logo-compact.png";
import ExpandIcon from "../assets/icons/expand.svg?react";
import ContentHeader from "../components/ContentHeader";
import DashboardNav from "../components/DashboardNav";
import { dashboardLayoutAtom } from "../stores/navItems";

export const outerLayoutExemptPattern =
    /\/(surveys\/submit|surveys\/preview|surveys\/create)/;

export const innerLayoutExemptPattern = /\/(login|shipments\/confirm)/;

const AdminLayout = (): ReactElement => {
    const isDesktop = useIsDesktop();
    const [layout, setLayout] = useAtom(dashboardLayoutAtom);
    const { pathname } = useLocation();

    useEffect(() => {
        setLayout(prev => ({
            ...prev,
            sbarExpanded: isDesktop,
        }));
    }, [isDesktop]);

    useHashRouteRedirect();

    const sbarWidth = layout.sbarExpanded ? 260 : 70;

    const outerLayoutSkipped =
        pathname.match(outerLayoutExemptPattern) || layout.isPrinting;

    const content = (
        <Suspense fallback={<p>Loading...</p>}>{useRoutes(routes)}</Suspense>
    );

    const innerLayoutSkipped = pathname.match(innerLayoutExemptPattern);

    const [isModalOpen, setModalOpen] = useState(false);
    const closeMobileModal = () => setModalOpen(false);

    if (outerLayoutSkipped) return content;

    const body = (
        <Box
            flexGrow={1}
            flexShrink={1}
            transition="margin-left 0.3s ease"
            position="relative"
            overflowX="hidden"
            className="popup-container"
            bg="rgb(247, 247, 247)"
        >
            <Flex
                direction="column"
                width="100%"
                height="100%"
                alignItems="stretch"
            >
                <ContentHeader
                    onMenuClick={() => setModalOpen(!isModalOpen)}
                    isMenuOpen={isModalOpen}
                    title={layout.pageTitle}
                />
                {!isDesktop && isModalOpen ? (
                    <Box overflowY="auto" maxHeight="100vh" position="relative">
                        <DashboardNav
                            closeMobileModal={closeMobileModal}
                            isCompact={false}
                        />
                    </Box>
                ) : (
                    content
                )}
            </Flex>
            <div className="popup-slot" />
            <Toaster />
        </Box>
    );

    if (innerLayoutSkipped) return body;

    return (
        <Flex
            height="100vh"
            position="relative"
            overflow={
                !isDesktop ? "scroll" : layout.isPrinting ? "visible" : "hidden"
            }
            background="rgba(247, 247, 247)"
        >
            {isDesktop ? (
                <Box
                    flexShrink="0"
                    height="100vh"
                    position="relative"
                    borderRightColor="gray.100"
                    borderRightWidth="1px"
                    borderRightStyle="solid"
                    transition="width 0.3s ease"
                    width={`${sbarWidth}px`}
                    className="screen-only"
                    background="white"
                >
                    <Link
                        position="absolute"
                        top="75px"
                        right="-15px"
                        zIndex="999"
                        style={{
                            rotate: layout.sbarExpanded ? "0deg" : "180deg",
                        }}
                        onClick={() => {
                            setLayout(prev => ({
                                ...prev,
                                sbarExpanded: !prev.sbarExpanded,
                            }));
                        }}
                    >
                        <ExpandIcon />
                    </Link>
                    <Box
                        overflow="hidden"
                        position="relative"
                        height="100%"
                        width={`${sbarWidth}px`}
                    >
                        <Box
                            overflowY="auto"
                            display="flex"
                            flexDirection="column"
                            height="100%"
                            width={`${sbarWidth}px`}
                        >
                            <SbarHeader
                                showMenu={!isDesktop}
                                isCompact={!layout.sbarExpanded}
                            />
                            <Box
                                flexGrow={1}
                                overflow="auto"
                                position="relative"
                                className="scroller"
                            >
                                <DashboardNav
                                    isCompact={!layout.sbarExpanded}
                                />
                            </Box>
                        </Box>
                    </Box>
                </Box>
            ) : null}
            {body}
        </Flex>
    );
};

export default AdminLayout;

function SbarHeader(p: { showMenu?: boolean; isCompact?: boolean }) {
    const setLayout = useSetAtom(dashboardLayoutAtom);
    return (
        <Box
            color="gray.500"
            textAlign="center"
            display="block"
            onClick={() => {
                setLayout(prev => ({
                    ...prev,
                    pageTitle: "Dashboard",
                }));
            }}
        >
            <a href="/">
                {p.isCompact ? (
                    <Box height="100px" paddingTop="20px">
                        <Image
                            src={compactLogo}
                            style={{
                                marginLeft: "auto",
                                marginRight: "auto",
                            }}
                        />
                    </Box>
                ) : (
                    <Image
                        src={appLogo}
                        style={{
                            width: "150px",
                            marginLeft: "auto",
                            marginRight: "auto",
                            marginTop: "10px",
                            marginBottom: "10px",
                            display: "block",
                        }}
                    />
                )}
            </a>
            {/* {p.showMenu
                ? (
                    <SidebarMenuButton
                        mt={4}
                        mr={4}
                    />
                )
                : null} */}
        </Box>
    );
}
